import { useEffect, useState } from "react";
import { MessageSquareWarning, Mail, Send, X, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUS_LABEL = { open: "Ouverte", in_progress: "En traitement", resolved: "Résolue" };
const STATUS_COLOR = {
    open: "bg-orange-100 text-orange-800",
    in_progress: "bg-blue-100 text-blue-800",
    resolved: "bg-green-100 text-green-800",
};
const TYPE_LABEL = {
    pedagogique: "Pédagogique",
    organisationnelle: "Organisationnelle",
    facturation: "Facturation",
    autre: "Autre",
};

function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function RespondDialog({ reclamation, onClose, onResponded }) {
    const [response, setResponse] = useState("");
    const [markResolved, setMarkResolved] = useState(true);
    const [sending, setSending] = useState(false);

    if (!reclamation) return null;

    const send = async () => {
        if (response.trim().length < 10) {
            toast.error("La réponse doit contenir au moins 10 caractères.");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post(`/admin/reclamations/${reclamation.id}/respond`, {
                response,
                mark_resolved: markResolved,
            });
            toast.success("Réponse envoyée (email mocké)");
            onResponded(data);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="respond-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="relative w-full max-w-2xl rounded-3xl bg-white p-8 shadow-premium-lg"
            >
                <button onClick={onClose} className="absolute right-5 top-5 text-brand-smoke hover:text-brand-ink" aria-label="Fermer">
                    <X className="h-5 w-5" />
                </button>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                    <Mail className="h-5 w-5" />
                </div>
                <h3 className="mt-6 font-display text-3xl">Répondre à la réclamation</h3>
                <p className="mt-2 text-sm text-brand-smoke">
                    De <strong>{reclamation.name}</strong> &lt;{reclamation.email}&gt; · {TYPE_LABEL[reclamation.type]}
                </p>
                <div className="mt-5 rounded-xl bg-[#FAFAFA] p-4 text-sm text-brand-ink/80">
                    <strong className="text-brand-ink">Réclamation :</strong> {reclamation.description}
                </div>
                <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    rows={8}
                    placeholder="Votre réponse au plaignant…"
                    data-testid="respond-textarea"
                    className="mt-5 w-full resize-none rounded-2xl border border-brand-line bg-white p-4 text-sm leading-relaxed outline-none focus:border-brand-orange"
                />
                <label className="mt-4 flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={markResolved}
                        onChange={(e) => setMarkResolved(e.target.checked)}
                        data-testid="respond-mark-resolved"
                    />
                    Marquer cette réclamation comme résolue
                </label>
                <div className="mt-5 flex items-center justify-end gap-3">
                    <button onClick={onClose} className="rounded-full px-5 py-2.5 text-sm hover:bg-brand-line/30">Annuler</button>
                    <button
                        onClick={send}
                        disabled={sending}
                        data-testid="respond-send"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                    >
                        <Send className="h-3.5 w-3.5" />
                        {sending ? "Envoi…" : "Envoyer la réponse"}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

export default function AdminReclamations() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [respondTo, setRespondTo] = useState(null);

    const fetchAll = async () => {
        try {
            const [list, c] = await Promise.all([
                api.get(`/admin/reclamations${statusFilter ? `?status=${statusFilter}` : ""}`),
                api.get("/admin/reclamations/counts"),
            ]);
            setItems(list.data);
            setCounts(c.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/reclamations/${id}`, { status });
            toast.success("Statut mis à jour");
            fetchAll();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-admin-reclamations">
            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">Démarche qualité</div>
                    <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">Réclamations</h1>
                    <p className="mt-3 max-w-2xl text-brand-smoke">
                        Suivi nominatif et tracking Qualiopi des remontées clients. Engagement : réponse sous 5 jours ouvrés.
                    </p>
                </div>
                <div className="hidden h-16 w-16 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange md:flex">
                    <MessageSquareWarning className="h-7 w-7" />
                </div>
            </div>

            {/* Counters */}
            {counts && (
                <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="reclamations-counters">
                    <div className="rounded-2xl border border-brand-line bg-white p-5 shadow-premium">
                        <div className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Total</div>
                        <div className="mt-2 font-display text-3xl">{counts.total}</div>
                    </div>
                    <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-orange-700">Ouvertes</div>
                        <div className="mt-2 font-display text-3xl text-orange-800">{counts.open}</div>
                    </div>
                    <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-blue-700">En traitement</div>
                        <div className="mt-2 font-display text-3xl text-blue-800">{counts.in_progress}</div>
                    </div>
                    <div className="rounded-2xl border border-green-200 bg-green-50 p-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-green-700">Résolues</div>
                        <div className="mt-2 font-display text-3xl text-green-800">{counts.resolved}</div>
                        {counts.resolution_rate_pct != null && (
                            <div className="mt-1 text-xs text-green-700">Taux : {counts.resolution_rate_pct}%</div>
                        )}
                    </div>
                </div>
            )}

            {/* Filter */}
            <div className="mt-6 inline-flex rounded-full border border-brand-line bg-white p-1">
                {[
                    { v: "", l: "Toutes" },
                    { v: "open", l: "Ouvertes" },
                    { v: "in_progress", l: "En traitement" },
                    { v: "resolved", l: "Résolues" },
                ].map((opt) => (
                    <button
                        key={opt.v}
                        onClick={() => setStatusFilter(opt.v)}
                        data-testid={`recl-filter-${opt.v || "all"}`}
                        className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                            statusFilter === opt.v ? "bg-brand-orange text-white" : "text-brand-smoke hover:text-brand-ink"
                        }`}
                    >
                        {opt.l}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="mt-10 text-center text-brand-smoke">Chargement…</div>
            ) : items.length === 0 ? (
                <div className="mt-10 rounded-2xl border border-dashed border-brand-line bg-white p-10 text-center text-brand-smoke">
                    Aucune réclamation {statusFilter || "à afficher"}.
                </div>
            ) : (
                <div className="mt-6 space-y-3">
                    {items.map((r) => (
                        <div key={r.id} className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid={`recl-row-${r.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_COLOR[r.status]}`}>
                                            {STATUS_LABEL[r.status]}
                                        </span>
                                        <span className="rounded-full bg-brand-line/50 px-2.5 py-0.5 text-[10px] font-semibold text-brand-ink/70">
                                            {TYPE_LABEL[r.type]}
                                        </span>
                                        <span className="text-xs text-brand-smoke">#{r.id.slice(0, 8)}</span>
                                    </div>
                                    <div className="mt-2 font-display text-xl text-brand-ink">{r.name}</div>
                                    <div className="text-xs text-brand-smoke">{r.email}{r.phone ? ` · ${r.phone}` : ""} · reçu le {formatDate(r.created_at)}</div>
                                    <p className="mt-3 text-sm text-brand-ink/80 line-clamp-3">{r.description}</p>
                                    {r.response_to_user && (
                                        <div className="mt-3 rounded-xl bg-green-50 p-3 text-xs text-green-900">
                                            <strong>Réponse envoyée le {formatDate(r.responded_at)} :</strong> {r.response_to_user}
                                        </div>
                                    )}
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-2">
                                    {r.status !== "resolved" && (
                                        <button
                                            onClick={() => setRespondTo(r)}
                                            data-testid={`recl-respond-${r.id}`}
                                            className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-4 py-2 text-xs font-semibold text-white hover:bg-[#d9772c]"
                                        >
                                            <Send className="h-3 w-3" /> Répondre
                                        </button>
                                    )}
                                    {r.status === "open" && (
                                        <button
                                            onClick={() => updateStatus(r.id, "in_progress")}
                                            data-testid={`recl-take-${r.id}`}
                                            className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-xs font-medium hover:border-brand-orange hover:text-brand-orange"
                                        >
                                            <Clock className="h-3 w-3" /> Prendre en charge
                                        </button>
                                    )}
                                    {r.status === "in_progress" && (
                                        <button
                                            onClick={() => updateStatus(r.id, "resolved")}
                                            className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-xs font-medium hover:border-green-700 hover:text-green-700"
                                        >
                                            <CheckCircle2 className="h-3 w-3" /> Marquer résolue
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <RespondDialog
                reclamation={respondTo}
                onClose={() => setRespondTo(null)}
                onResponded={fetchAll}
            />
        </div>
    );
}
